import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { useRefreshEffect } from "@/context/RefreshContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import FormDialog from "@/components/FormDialog";
import { UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Archive, ArchiveRestore, Trash2, Inbox, CheckCircle2, XCircle, Download, ArrowDownWideNarrow, ArrowUpNarrowWide, ArrowUpDown, Handshake, Lock, RotateCcw } from "lucide-react";
import {
  GEFAHRENSTUFE_VALUES,
  GEFAHRENSTUFE_COLOR,
  ORTUNG_VALUES,
  ORTUNG_COLOR,
  HOECHSTSTRAFEN,
  HOECHSTSTRAFE_MONTHS,
  formatDatum,
  todayISO,
} from "@/lib/constants";

const GEFAHR_BADGE = {
  green: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  orange: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  red: "text-red-400 border-red-500/40 bg-red-500/10",
};
const NAME_BG = {
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  red: "bg-red-500/15 text-red-300 border-red-500/30",
  state: "bg-slate-500/20 text-slate-200 border-slate-400/40",
};
const DOT = { green: "bg-emerald-400", yellow: "bg-yellow-400", red: "bg-red-400" };
const TIME_TEXT = { green: "text-emerald-400", yellow: "text-yellow-400", red: "text-red-400", state: "text-slate-300" };

const OrtMarker = ({ o }) =>
  o === "State" ? (
    <Lock className="h-3.5 w-3.5 text-slate-300" />
  ) : (
    <span className={`h-2.5 w-2.5 rounded-full ${DOT[ORTUNG_COLOR[o]] || "bg-zinc-500"}`} />
  );

const formatOrtungZeit = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return "";
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Verjährung anzeigen: bei Bewährung mit Uhrzeit (falls gesetzt), sonst nur Datum.
const formatVerjaehrung = (v) => {
  if (!v) return "—";
  const hasTime = typeof v === "string" && v.length > 10 && v.includes("T");
  if (!hasTime) return formatDatum(v);
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " Uhr";
};

// datetime-local braucht "YYYY-MM-DDTHH:MM"; reines Datum -> Mitternacht ergänzen.
const verjaehrungInputVal = (v) => {
  if (!v) return "";
  return v.length === 10 ? `${v}T00:00` : v.slice(0, 16);
};

const RESOZ_FUNKTIONEN = ["Bewährung", "Resozialisierung"];

const emptyParole = (bereich = "parole") => ({
  gefahrenstufe: "Gefahrenstufe 1",
  name: "",
  agv: "",
  telefonnummer: "",
  letzte_ortung: "",
  ortung_zeit: "",
  akte: "",
  hoechststrafe: bereich === "resozialisierung" ? "Bewährung" : "",
  wanted_seit: todayISO(),
  perso_akte: "",
  schnatter: "",
  arbeitet_bei: "",
  info: "",
  haftantritt_id: "",
  bereich,
});

function LinkCell({ value }) {
  if (value && /^https?:\/\//i.test(value))
    return (
      <a href={value} target="_blank" rel="noreferrer" className="text-primary hover:underline whitespace-nowrap">
        Öffnen ↗
      </a>
    );
  return value ? <span className="break-all">{value}</span> : <span className="text-zinc-600">—</span>;
}

export default function ParoleView({ mode = "aktiv", testidPrefix = "parole", bereich = "parole" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(emptyParole());
  const [submitting, setSubmitting] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regGangs, setRegGangs] = useState([]);
  const [regValues, setRegValues] = useState({ name: "", agv: "", agv_unklar: false, telefonnummer: "", perso_akte: "", schnatter: "", arbeitet_bei: "" });
  const [regSubmitting, setRegSubmitting] = useState(false);

  useEffect(() => {
    api.get("/gangregister").then((r) => setRegGangs(r.data.map((g) => g.name))).catch(() => {});
  }, []);

  const regFields = [
    { name: "name", label: "Name", type: "text", required: true },
    { name: "agv", label: "AGV (Gang)", type: "select", options: regGangs, clearable: true },
    { name: "agv_unklar", label: "AGV unklar (zeigt „AGV-Unklar“ vor dem Gangnamen)", type: "checkbox" },
    { name: "telefonnummer", label: "Telefonnummer", type: "text" },
    { name: "perso_akte", label: "Perso-Akte (Link)", type: "text", placeholder: "https://…", required: true },
    { name: "schnatter", label: "Schnatter (Link)", type: "text", placeholder: "https://…" },
    { name: "arbeitet_bei", label: "Arbeitet bei", type: "text" },
  ];

  const openReg = () => {
    setRegValues({ name: values.name || "", agv: "", agv_unklar: false, telefonnummer: "", perso_akte: "", schnatter: "", arbeitet_bei: "" });
    setRegOpen(true);
  };

  const submitReg = async () => {
    if (!regValues.name || !regValues.perso_akte) {
      toast.error("Name und Perso-Akte sind Pflichtfelder");
      return;
    }
    setRegSubmitting(true);
    try {
      await api.post("/verbrecherregister", regValues);
      toast.success("Person im Verbrecherregister angelegt");
      setRegOpen(false);
      setValues((v) => ({ ...v, name: regValues.name }));
      lookup(regValues.name);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Anlegen fehlgeschlagen");
    } finally {
      setRegSubmitting(false);
    }
  };
  const [confirm, setConfirm] = useState(null); // {type, row}
  const [sort, setSort] = useState(null); // null | "neu" | "alt" (nach Wanted seit)
  const [gangDeals, setGangDeals] = useState({}); // AGV-Name -> Deal-Text (nur Gangs mit Deal)

  const isArchive = mode !== "aktiv";
  const isResoz = bereich === "resozialisierung";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/parole", { params: { status: mode, bereich } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [mode, bereich]);

  useEffect(() => {
    load();
  }, [load]);

  useRefreshEffect(load);

  const loadGangDeals = useCallback(async () => {
    try {
      const { data } = await api.get("/gangregister");
      const map = {};
      data.forEach((g) => {
        if (g.name && (g.deals || "").trim()) map[g.name] = g.deals.trim();
      });
      setGangDeals(map);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadGangDeals();
  }, [loadGangDeals]);

  useRefreshEffect(loadGangDeals);

  // Vorbelegung aus Haftantritt „Nicht Erschienen": Parole-Fenster öffnen + Register-Lookup.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const preName = location.state?.createParoleName;
    const preHaftId = location.state?.createParoleHaftantrittId || "";
    if (preName && mode === "aktiv") {
      setEditing(null);
      setValues({ ...emptyParole(bereich), name: preName, haftantritt_id: preHaftId });
      setDialogOpen(true);
      lookup(preName);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setValues(emptyParole(bereich));
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setValues({ ...emptyParole(bereich), ...row });
    setDialogOpen(true);
  };

  const onHoechststrafe = (v) => {
    setValues((prev) => ({
      ...prev,
      hoechststrafe: v,
      gefahrenstufe: v === "Mord" ? "Gefahrenstufe 3" : prev.gefahrenstufe,
    }));
  };

  const lookup = async (nameArg) => {
    const name = (typeof nameArg === "string" ? nameArg : values.name || "").trim();
    if (!name) return;
    try {
      const { data } = await api.get("/verbrecherregister/lookup", { params: { name } });
      if (data.found) {
        const p = data.person;
        setValues((v) => ({
          ...v,
          agv: p.agv || v.agv,
          telefonnummer: p.telefonnummer || v.telefonnummer,
          perso_akte: p.perso_akte || v.perso_akte,
          schnatter: p.schnatter || v.schnatter,
          arbeitet_bei: p.arbeitet_bei || v.arbeitet_bei,
        }));
        toast.success("Registerdaten übernommen");
      } else {
        toast.message("Keine Registerdaten gefunden", { description: "Person ist nicht im Verbrecherregister." });
      }
    } catch (e) {
      /* ignore */
    }
  };

  const verjaehrungPreview = () => {
    if (values.hoechststrafe === "Mord") return formatDatum("9999-12-31");
    const months = HOECHSTSTRAFE_MONTHS[values.hoechststrafe];
    if (!months || !values.wanted_seit) return "—";
    const d = new Date(values.wanted_seit);
    if (isNaN(d)) return "—";
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    if (d.getDate() < day) d.setDate(0);
    d.setDate(d.getDate() + 1);
    return formatDatum(d.toISOString().slice(0, 10));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!values.hoechststrafe) {
      toast.error("Bitte eine Höchststrafe auswählen");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...values, bereich };
      delete payload.id;
      if (!isResoz && values.hoechststrafe !== "Bewährung") delete payload.verjaehrung;
      delete payload.archived;
      delete payload.archive_status;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.created_by;
      if (editing) {
        await api.put(`/parole/${editing.id}`, payload);
        toast.success("Parole aktualisiert");
      } else {
        await api.post("/parole", payload);
        toast.success("Parole erstellt");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const updateOrtung = async (row, value) => {
    const ortung_zeit = value ? new Date().toISOString() : "";
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, letzte_ortung: value, ortung_zeit } : r)));
    try {
      const payload = { ...row, letzte_ortung: value, ortung_zeit };
      ["id", "verjaehrung", "archived", "archive_status", "created_at", "updated_at", "created_by"].forEach((k) => delete payload[k]);
      await api.put(`/parole/${row.id}`, payload);
      toast.success(value ? `Ortung „${value}" gesetzt · ${formatOrtungZeit(ortung_zeit)}` : "Letzte Ortung zurückgesetzt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aktualisierung fehlgeschlagen");
      load();
    }
  };

  const archive = async (row, status) => {    try {
      await api.patch(`/parole/${row.id}/archive`, null, { params: { status } });
      toast.success(status === "erfolgreich" ? "Als erfolgreich archiviert" : "Als fehlgeschlagen archiviert");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehlgeschlagen");
    }
  };

  const doConfirm = async () => {
    if (!confirm) return;
    const { type, row } = confirm;
    if (type === "ortung") {
      setConfirm(null);
      updateOrtung(row, confirm.value);
      return;
    }
    try {
      if (type === "delete") {
        await api.delete(`/parole/${row.id}`);
        toast.success("Eintrag gelöscht");
      } else if (type === "restore") {
        await api.patch(`/parole/${row.id}/restore`);
        toast.success("Wiederhergestellt");
      } else if (type === "reset-all") {
        const { data } = await api.post(`/parole/reset-ortung`, null, { params: { bereich } });
        toast.success(`${data.reset} Ortung(en) zurückgesetzt`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setConfirm(null);
    }
  };

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return ["name", "agv", "telefonnummer", "hoechststrafe", "info", "arbeitet_bei"].some((k) =>
      String(r[k] ?? "").toLowerCase().includes(q)
    );
  });

  const sorted = sort
    ? [...filtered].sort((a, b) => {
        const da = new Date(a.wanted_seit).getTime() || 0;
        const db = new Date(b.wanted_seit).getTime() || 0;
        return sort === "neu" ? db - da : da - db;
      })
    : filtered;

  const ALL_COLUMNS = [
    { key: "gefahrenstufe", label: "Gefahrenstufe", cell: (r) => r.gefahrenstufe ? <Badge className={`rounded-sm border font-mono text-[11px] whitespace-nowrap ${GEFAHR_BADGE[GEFAHRENSTUFE_COLOR[r.gefahrenstufe]] || ""}`}>{r.gefahrenstufe}</Badge> : "—" },
    { key: "name", label: "Name", className: "whitespace-nowrap", cell: (r) => { const oc = ORTUNG_COLOR[r.letzte_ortung]; const isState = r.letzte_ortung === "State"; return oc ? <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-sm font-medium whitespace-nowrap ${NAME_BG[oc]}`}>{isState && <Lock className="h-3.5 w-3.5 shrink-0" />}{r.name}</span> : <span className="text-sm text-zinc-200 whitespace-nowrap">{r.name}</span>; } },
    { key: "agv", label: "AGV", className: "font-mono text-sm text-zinc-300 align-top", cell: (r) => {
      if (!r.agv) return "—";
      const deal = gangDeals[r.agv];
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          {r.agv}
          {deal && (
            <span data-testid={`${testidPrefix}-deal-${r.id}`} title={`Deal mit ${r.agv}: ${deal}`} className="inline-flex items-center">
              <Handshake className="h-4 w-4 text-amber-400" />
            </span>
          )}
        </span>
      );
    } },
    { key: "telefonnummer", label: "Telefon", className: "font-mono text-sm text-zinc-300 align-top whitespace-nowrap", cell: (r) => r.telefonnummer || "—" },
    { key: "letzte_ortung", label: "Letzte Ortung", cell: (r) => {
      const oc = ORTUNG_COLOR[r.letzte_ortung];
      const zeit = r.ortung_zeit ? formatOrtungZeit(r.ortung_zeit) : "";
      if (isArchive) {
        return (
          <div className="space-y-1">
            {r.letzte_ortung ? (
              <span className="inline-flex items-center gap-2 text-sm text-zinc-300 whitespace-nowrap">
                <OrtMarker o={r.letzte_ortung} />{r.letzte_ortung}
              </span>
            ) : <span className="text-zinc-600">—</span>}
            {zeit && <span className={`block font-mono text-[10px] whitespace-nowrap ${TIME_TEXT[oc] || "text-zinc-500"}`}>seit {zeit}</span>}
          </div>
        );
      }
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <Select value={r.letzte_ortung || ""}>
              <SelectTrigger data-testid={`${testidPrefix}-ortung-${r.id}`} className="h-8 w-[130px] bg-transparent border-zinc-700 rounded-sm focus:ring-primary text-sm">
                <SelectValue placeholder="—">
                  {r.letzte_ortung ? (
                    <span className="inline-flex items-center gap-2 whitespace-nowrap">
                      <OrtMarker o={r.letzte_ortung} />
                      {r.letzte_ortung}
                    </span>
                  ) : "—"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
                {ORTUNG_VALUES.map((o) => (
                  <SelectItem
                    key={o}
                    value={o}
                    className="rounded-sm text-sm"
                    onPointerUp={() => setConfirm({ type: "ortung", row: r, value: o })}
                  >
                    <span className="inline-flex items-center gap-2">
                      <OrtMarker o={o} />
                      {o}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {zeit && (
            <span data-testid={`${testidPrefix}-ortung-zeit-${r.id}`} className={`block font-mono text-[10px] whitespace-nowrap ${TIME_TEXT[oc] || "text-zinc-500"}`}>
              seit {zeit}
            </span>
          )}
        </div>
      );
    } },
    { key: "hoechststrafe", label: isResoz ? "Funktion" : "Höchststrafe", className: "text-sm text-zinc-300 align-top whitespace-nowrap", cell: (r) => r.hoechststrafe || "—" },
    { key: "wanted_seit", label: "Wanted seit", className: "font-mono text-sm text-zinc-300 align-top whitespace-nowrap", cell: (r) => formatDatum(r.wanted_seit) },
    { key: "verjaehrung", label: "Verjährung", className: "font-mono text-sm text-zinc-300 align-top whitespace-nowrap", cell: (r) => formatVerjaehrung(r.verjaehrung) },
    { key: "akte", label: "Akte", className: "text-sm align-top", cell: (r) => <LinkCell value={r.akte} /> },
    { key: "perso_akte", label: "Perso-Akte", className: "text-sm align-top", cell: (r) => <LinkCell value={r.perso_akte} /> },
    { key: "schnatter", label: "Schnatter", className: "text-sm align-top", cell: (r) => <LinkCell value={r.schnatter} /> },
    { key: "arbeitet_bei", label: "Arbeitet bei", className: "text-sm text-zinc-300 align-top whitespace-nowrap", cell: (r) => r.arbeitet_bei || "—" },
    { key: "info", label: "Info", className: "text-sm text-zinc-300 align-top", cell: (r) => <span className="block max-w-xs whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{r.info || "—"}</span> },
  ];
  const columns = isArchive
    ? ALL_COLUMNS.filter((c) => ["gefahrenstufe", "name", "letzte_ortung", "agv", "akte"].includes(c.key))
    : ALL_COLUMNS;

  const renderRow = (r, dim = false) => {
    const isBewaehrung = r.hoechststrafe === "Bewährung";
    const isVerjaehrt = r.verjaehrung && r.verjaehrung.slice(0, 10) <= todayISO();
    const rowClass = isVerjaehrt
      ? "bg-red-500/20 hover:bg-red-500/30"
      : isBewaehrung
      ? "bg-blue-500/15 hover:bg-blue-500/25"
      : "hover:bg-zinc-900/50";
    return (
      <TableRow key={r.id} data-testid={`${testidPrefix}-row`} className={`border-zinc-800 transition-colors duration-150 ${rowClass} ${dim ? "opacity-60" : ""}`}>
        {columns.map((c) => (
          <TableCell key={c.key} className={c.className || "align-top"}>{c.cell(r)}</TableCell>
        ))}
        <TableCell className="text-right align-top">
          <div className="flex items-center justify-end gap-1">
            {!isArchive && (
              <>
                <Button data-testid={`${testidPrefix}-edit`} size="icon" variant="ghost" onClick={() => openEdit(r)} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-primary hover:bg-primary/10" title="Bearbeiten">
                  <Pencil className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid={`${testidPrefix}-archive`} size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10" title="Archivieren">
                      <Archive className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
                    <DropdownMenuItem data-testid={`${testidPrefix}-archive-erfolgreich`} onClick={() => archive(r, "erfolgreich")} className="rounded-sm text-emerald-400 focus:text-emerald-300 focus:bg-emerald-500/10 gap-2">
                      <CheckCircle2 className="h-4 w-4" /> Erfolgreich
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`${testidPrefix}-archive-fehlgeschlagen`} onClick={() => archive(r, "fehlgeschlagen")} className="rounded-sm text-red-400 focus:text-red-300 focus:bg-red-500/10 gap-2">
                      <XCircle className="h-4 w-4" /> Fehlgeschlagen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            {isArchive && (
              <Button data-testid={`${testidPrefix}-restore`} size="icon" variant="ghost" onClick={() => setConfirm({ type: "restore", row: r })} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10" title="Wiederherstellen">
                <ArchiveRestore className="h-4 w-4" />
              </Button>
            )}
            <Button data-testid={`${testidPrefix}-delete`} size="icon" variant="ghost" onClick={() => setConfirm({ type: "delete", row: r })} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10" title="Löschen">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid={`${testidPrefix}-search`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen…"
            className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              data-testid={`${testidPrefix}-sort-btn`}
              variant="outline"
              className="rounded-sm border-zinc-700 gap-2 text-zinc-300"
            >
              {sort === "neu" ? <ArrowDownWideNarrow className="h-4 w-4" /> : sort === "alt" ? <ArrowUpNarrowWide className="h-4 w-4" /> : <ArrowUpDown className="h-4 w-4" />}
              {sort === "neu" ? "Neueste zuerst" : sort === "alt" ? "Älteste zuerst" : "Sortieren"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
            <DropdownMenuItem data-testid={`${testidPrefix}-sort-neu`} onClick={() => setSort("neu")} className="rounded-sm gap-2 text-zinc-300 focus:bg-zinc-800">
              <ArrowDownWideNarrow className="h-4 w-4" /> Wanted seit – Neueste zuerst
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`${testidPrefix}-sort-alt`} onClick={() => setSort("alt")} className="rounded-sm gap-2 text-zinc-300 focus:bg-zinc-800">
              <ArrowUpNarrowWide className="h-4 w-4" /> Wanted seit – Älteste zuerst
            </DropdownMenuItem>
            {sort && (
              <DropdownMenuItem data-testid={`${testidPrefix}-sort-reset`} onClick={() => setSort(null)} className="rounded-sm gap-2 text-zinc-500 focus:bg-zinc-800">
                <ArrowUpDown className="h-4 w-4" /> Sortierung zurücksetzen
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {!isArchive && (
          <Button
            data-testid={`${testidPrefix}-reset-all-btn`}
            variant="outline"
            onClick={() => setConfirm({ type: "reset-all" })}
            className="rounded-sm border-zinc-700 gap-2 text-zinc-300 hover:text-red-400 hover:border-red-500/50"
            title="Alle letzten Ortungen zurücksetzen"
          >
            <RotateCcw className="h-4 w-4" /> Ortungen zurücksetzen
          </Button>
        )}
        {!isArchive && (
          <Button
            data-testid={`${testidPrefix}-create-btn`}
            onClick={openCreate}
            className="rounded-sm bg-primary hover:bg-blue-600 text-white gap-2"
          >
            <Plus className="h-4 w-4" />
            Parole erstellen
          </Button>
        )}
      </div>

      <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-hidden">
        <Table wrapperClassName="max-h-[calc(100vh_-_15rem)]" className="min-w-[900px]">
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              {columns.map((c) => (
                <TableHead key={c.key} className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500 h-11 whitespace-nowrap sticky top-0 z-20 bg-[#0C0E12]">
                  {c.label}
                </TableHead>
              ))}
              <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500 whitespace-nowrap sticky top-0 z-20 bg-[#0C0E12]">
                Aktionen
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={columns.length + 1} className="text-center py-10 text-zinc-500 font-mono text-sm">
                  Wird geladen…
                </TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={columns.length + 1} className="text-center py-14">
                  <div className="flex flex-col items-center gap-2 text-zinc-600">
                    <Inbox className="h-8 w-8" />
                    <span className="font-mono text-sm">Keine Einträge vorhanden</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((r) => renderRow(r))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit dialog */}
      <FormDialog
        open={regOpen}
        onOpenChange={setRegOpen}
        title="Person hinzufügen"
        fields={regFields}
        values={regValues}
        setValues={setRegValues}
        onSubmit={submitReg}
        submitting={regSubmitting}
        testidPrefix="parole-register"
      />
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-zinc-100">
              {editing ? "Parole bearbeiten" : "Parole erstellen"}
            </DialogTitle>
          </DialogHeader>
          {!editing && (
            <Button
              type="button"
              variant="outline"
              data-testid="parole-open-register"
              onClick={openReg}
              className="w-full rounded-sm border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 gap-2 mb-1"
            >
              <UserPlus className="h-4 w-4" />
              Person im Verbrecherregister anlegen
            </Button>
          )}
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Name + lookup */}
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Name <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <Input data-testid={`${testidPrefix}-name`} value={values.name} onChange={(e) => setField("name", e.target.value)} onBlur={lookup} required className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
                  <Button type="button" variant="outline" onClick={lookup} data-testid={`${testidPrefix}-lookup`} className="rounded-sm border-zinc-700 gap-2 shrink-0">
                    <Download className="h-4 w-4" /> Register
                  </Button>
                </div>
              </div>

              <Field label="Gefahrenstufe">
                <SelectBox testid={`${testidPrefix}-gefahrenstufe`} value={values.gefahrenstufe} onChange={(v) => setField("gefahrenstufe", v)} options={GEFAHRENSTUFE_VALUES} disabled={values.hoechststrafe === "Mord"} />
                {values.hoechststrafe === "Mord" && <p className="text-[10px] text-red-400 mt-1">Bei Mord automatisch Stufe 3.</p>}
              </Field>
              {isResoz ? (
                <Field label="Funktion" required>
                  <SelectBox testid={`${testidPrefix}-hoechststrafe`} value={values.hoechststrafe} onChange={onHoechststrafe} options={RESOZ_FUNKTIONEN} />
                </Field>
              ) : (
                <Field label="Höchststrafe" required>
                  <SelectBox testid={`${testidPrefix}-hoechststrafe`} value={values.hoechststrafe} onChange={onHoechststrafe} options={HOECHSTSTRAFEN} />
                </Field>
              )}

              <Field label="AGV"><TextInput testid={`${testidPrefix}-agv`} value={values.agv} onChange={(v) => setField("agv", v)} /></Field>
              <Field label="Telefonnummer"><TextInput testid={`${testidPrefix}-telefonnummer`} value={values.telefonnummer} onChange={(v) => setField("telefonnummer", v)} /></Field>

              <Field label="Letzte Ortung">
                <SelectBox testid={`${testidPrefix}-letzte_ortung`} value={values.letzte_ortung} onChange={(v) => setField("letzte_ortung", v)} options={ORTUNG_VALUES} />
              </Field>
              <Field label="Wanted seit"><TextInput type="date" testid={`${testidPrefix}-wanted_seit`} value={values.wanted_seit} onChange={(v) => setField("wanted_seit", v)} /></Field>

              <Field label={isResoz ? "Ziel-Datum (mit Uhrzeit)" : (values.hoechststrafe === "Bewährung" ? "Verjährung (manuell, mit Uhrzeit)" : "Verjährung (automatisch)")}>
                {(isResoz || values.hoechststrafe === "Bewährung") ? (
                  <TextInput type="datetime-local" testid={`${testidPrefix}-verjaehrung`} value={verjaehrungInputVal(values.verjaehrung)} onChange={(v) => setField("verjaehrung", v)} />
                ) : (
                  <div data-testid={`${testidPrefix}-verjaehrung`} className="h-10 flex items-center px-3 border border-zinc-800 rounded-sm bg-zinc-900/40 font-mono text-sm text-zinc-300">
                    {verjaehrungPreview()}
                  </div>
                )}
              </Field>
              <Field label="Arbeitet bei"><TextInput testid={`${testidPrefix}-arbeitet_bei`} value={values.arbeitet_bei} onChange={(v) => setField("arbeitet_bei", v)} /></Field>

              <Field label="Akte (Link)"><TextInput testid={`${testidPrefix}-akte`} value={values.akte} onChange={(v) => setField("akte", v)} placeholder="https://…" /></Field>
              <Field label="Perso-Akte (Link)" required><TextInput testid={`${testidPrefix}-perso_akte`} value={values.perso_akte} onChange={(v) => setField("perso_akte", v)} placeholder="https://…" required /></Field>
              <Field label="Schnatter (Link)"><TextInput testid={`${testidPrefix}-schnatter`} value={values.schnatter} onChange={(v) => setField("schnatter", v)} placeholder="https://…" /></Field>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Info / Grund Wanted</Label>
              <Textarea data-testid={`${testidPrefix}-info`} value={values.info} onChange={(e) => setField("info", e.target.value)} rows={3} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary resize-none" />
            </div>

            <DialogFooter className="pt-2 gap-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="rounded-sm text-zinc-400">Abbrechen</Button>
              <Button type="submit" disabled={submitting} data-testid={`${testidPrefix}-submit`} className="rounded-sm bg-primary hover:bg-blue-600 text-white">
                {submitting ? "Speichern…" : "Speichern"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">
              {confirm?.type === "ortung" ? "Letzte Ortung richtig?" : confirm?.type === "delete" ? "Eintrag löschen?" : confirm?.type === "reset-all" ? "Alle Ortungen zurücksetzen?" : "Eintrag wiederherstellen?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirm?.type === "ortung"
                ? <span data-testid={`${testidPrefix}-ortung-confirm-text`}>Soll bei „{confirm.row?.name}“ die Ortung <strong className="text-zinc-100">„{confirm.value}“</strong> gesetzt werden?</span>
                : confirm?.type === "delete"
                ? "Dieser Vorgang kann nicht rückgängig gemacht werden."
                : confirm?.type === "reset-all"
                ? "Die „Letzte Ortung“ wird bei ALLEN aktiven Parole-Einträgen entfernt."
                : "Der Eintrag wird zurück in den aktiven Bereich verschoben."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testidPrefix}-confirm`} onClick={doConfirm} className={`rounded-sm ${confirm?.type === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-blue-600"} text-white`}>
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, type = "text", placeholder, testid, required }) {
  return (
    <Input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ""} required={required} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
  );
}

function SelectBox({ value, onChange, options, disabled, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid} className="bg-transparent border-zinc-700 rounded-sm focus:ring-primary">
        <SelectValue placeholder="Auswählen…" />
      </SelectTrigger>
      <SelectContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
        {options.map((o) => (
          <SelectItem key={o} value={o} className="rounded-sm text-sm">{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
